import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import apiClient from '../../../../api/apiClient';
import { showToast } from '../../../../utils/toast';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const DistributorDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    totalFOS: 0,
    activeFOS: 0,
    inactiveFOS: 0,
    totalRetailers: 0,
    activeRetailers: 0,
    inactiveRetailers: 0,
    totalOutstanding: 0,
    totalCollection: 0,
    todayCollection: 0,
    thisMonthCollection: 0,
    registrationTrends: {
      today: 0,
      thisWeek: 0,
      thisMonth: 0
    }
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [registrationTrends, setRegistrationTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartPeriod, setChartPeriod] = useState('daily');

  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    fetchRegistrationTrends();
  }, [dateRange, chartPeriod]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch overview data
      const [overviewResponse, activitiesResponse] = await Promise.all([
        apiClient.get('/api/distributor/dashboard/overview/'),
        apiClient.get('/api/distributor/dashboard/recent-activities/', {
          params: { limit: 5 }
        })
      ]);

      if (overviewResponse.data.success) {
        const overview = overviewResponse.data.data;
        setDashboardData({
          totalFOS: overview.user_counts.fos.total || 0,
          activeFOS: overview.user_counts.fos.active || 0,
          inactiveFOS: overview.user_counts.fos.inactive || 0,
          totalRetailers: overview.user_counts.retailers.total || 0,
          activeRetailers: overview.user_counts.retailers.active || 0,
          inactiveRetailers: overview.user_counts.retailers.inactive || 0,
          totalOutstanding: overview.summary?.total_outstanding || 0,
          totalCollection: overview.summary?.total_collection || 0,
          todayCollection: overview.summary?.today_collection || 0,
          thisMonthCollection: overview.summary?.this_month_collection || 0,
          registrationTrends: {
            today: overview.summary.today_registrations || 0,
            thisWeek: overview.summary.this_week_registrations || 0,
            thisMonth: overview.summary.this_month_registrations || 0
          }
        });
      }

      if (activitiesResponse.data.success) {
        setRecentActivities(activitiesResponse.data.data.activities || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showToast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrationTrends = async () => {
    try {
      const response = await apiClient.get('/api/distributor/dashboard/registration-trends/', {
        params: {
          period: chartPeriod,
          start_date: dateRange.startDate,
          end_date: dateRange.endDate
        }
      });

      if (response.data.success) {
        setRegistrationTrends(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching registration trends:', error);
      showToast.error('Failed to fetch registration trends');
    }
  };

  const StatCard = ({ icon, title, value, subtitle, iconColor = "#031a6f", valuePrefix = "" }) => (
    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
      <Card.Body style={{ background: '#ffffff' }}>
        <div className="d-flex justify-content-between align-items-start">
          <div className="text-dark">
            <div className="fs-6 mb-2 text-muted">{title}</div>
            <div className="fs-2 fw-bold mb-1" style={{ color: '#031a6f' }}>
              {valuePrefix}{typeof value === 'number' && valuePrefix === '₹' ? value.toLocaleString() : value}
            </div>
            {subtitle && <div className="fs-7 text-muted">{subtitle}</div>}
          </div>
          <div className="p-3 rounded-circle" style={{ backgroundColor: `${iconColor}15` }}>
            <i className={`${icon} fs-4`} style={{ color: iconColor }}></i>
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getActivityIcon = (activity) => {
    // Use icon_color from API if available
    const colorMap = {
      'success': 'success',
      'primary': 'primary',
      'warning': 'warning',
      'info': 'info',
      'danger': 'danger'
    };
    
    const iconMap = {
      'user-plus': 'fas fa-user-plus',
      'money-bill-wave': 'fas fa-money-bill-wave',
      'exclamation-circle': 'fas fa-exclamation-circle',
      'file-upload': 'fas fa-file-upload',
      'clock': 'fas fa-clock'
    };
    
    return {
      icon: iconMap[activity.icon] || 'fas fa-clock',
      color: colorMap[activity.icon_color] || 'secondary'
    };
  };

  const formatTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  // Prepare chart data
  const chartData = registrationTrends && registrationTrends.trends ? {
    labels: registrationTrends.trends.map(trend => {
      const date = new Date(trend.date);
      if (chartPeriod === 'daily') {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else if (chartPeriod === 'weekly') {
        return `Week ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }
    }),
    datasets: [
      {
        label: 'FOS Registrations',
        data: registrationTrends.trends.map(trend => trend.fos),
        borderColor: 'rgb(5, 150, 105)',
        backgroundColor: 'rgba(5, 150, 105, 0.1)',
        tension: 0.4
      },
      {
        label: 'Retailer Registrations',
        data: registrationTrends.trends.map(trend => trend.retailers),
        borderColor: 'rgb(220, 38, 38)',
        backgroundColor: 'rgba(220, 38, 38, 0.1)',
        tension: 0.4
      },
      {
        label: 'Total Registrations',
        data: registrationTrends.trends.map(trend => trend.total),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4
      }
    ]
  } : null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-1">
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-tachometer-alt me-2"></i>
          Distributor Dashboard
        </h5>
        
        {/* Date Range Filter */}
        <div className="d-flex gap-2 align-items-center">
          <span className="text-muted">Filter Period:</span>
          <Form.Control
            type="date"
            value={dateRange.startDate}
            onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
            style={{ width: '150px' }}
          />
          <span className="text-muted">to</span>
          <Form.Control
            type="date"
            value={dateRange.endDate}
            onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
            style={{ width: '150px' }}
          />
          <Button variant="primary" onClick={() => {
            fetchDashboardData();
            fetchRegistrationTrends();
          }}>
            <i className="fas fa-search me-1"></i>
            Apply
          </Button>
        </div>
      </div>

      {/* Main Metrics Row */}
      <Row className="g-4 mb-4">
        <Col lg={3} md={6}>
          <StatCard
            icon="fas fa-users"
            title="Total FOS"
            value={dashboardData.totalFOS}
            subtitle={`Active: ${dashboardData.activeFOS} | Inactive: ${dashboardData.inactiveFOS}`}
            iconColor="#059669"
          />
        </Col>
        <Col lg={3} md={6}>
          <StatCard
            icon="fas fa-store"
            title="Total Retailers"
            value={dashboardData.totalRetailers}
            subtitle={`Active: ${dashboardData.activeRetailers} | Inactive: ${dashboardData.inactiveRetailers}`}
            iconColor="#dc2626"
          />
        </Col>
        <Col lg={3} md={6}>
          <StatCard
            icon="fas fa-exclamation-triangle"
            title="Outstanding"
            value={dashboardData.totalOutstanding}
            valuePrefix="₹"
            subtitle="Pending Collections"
            iconColor="#f59e0b"
          />
        </Col>
        <Col lg={3} md={6}>
          <StatCard
            icon="fas fa-coins"
            title="Total Collection"
            value={dashboardData.totalCollection}
            valuePrefix="₹"
            subtitle="Period Collection"
            iconColor="#10b981"
          />
        </Col>
      </Row>

      {/* Collection Metrics Row */}
      <Row className="g-4 mb-4">
        <Col lg={6} md={6}>
          <StatCard
            icon="fas fa-calendar-day"
            title="Today's Collection"
            value={dashboardData.todayCollection}
            valuePrefix="₹"
            subtitle={new Date().toLocaleDateString()}
            iconColor="#3b82f6"
          />
        </Col>
        <Col lg={6} md={6}>
          <StatCard
            icon="fas fa-calendar-alt"
            title="This Month Collection"
            value={dashboardData.thisMonthCollection}
            valuePrefix="₹"
            subtitle={new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            iconColor="#8b5cf6"
          />
        </Col>
      </Row>

      {/* Charts and Summary Row */}
      <Row className="g-4">
        <Col lg={8}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0" style={{ color: '#031a6f', fontWeight: '600' }}>
                  <i className="fas fa-chart-line me-2"></i>
                  Registration Trends
                </h5>
                <div className="btn-group btn-group-sm" role="group">
                  <button 
                    type="button" 
                    className={`btn ${chartPeriod === 'daily' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setChartPeriod('daily')}
                  >
                    Daily
                  </button>
                  <button 
                    type="button" 
                    className={`btn ${chartPeriod === 'weekly' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setChartPeriod('weekly')}
                  >
                    Weekly
                  </button>
                  <button 
                    type="button" 
                    className={`btn ${chartPeriod === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setChartPeriod('monthly')}
                  >
                    Monthly
                  </button>
                </div>
              </div>
              {chartData ? (
                <Line data={chartData} options={chartOptions} />
              ) : (
                <div className="text-center py-5">
                  <Spinner animation="border" size="sm" />
                  <p className="text-muted mt-2">Loading chart data...</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <Card.Body>
              <h5 className="mb-4" style={{ color: '#031a6f', fontWeight: '600' }}>
                <i className="fas fa-clock me-2"></i>
                Recent Activities
              </h5>
              <div className="d-flex flex-column gap-3">
                {recentActivities.length > 0 ? (
                  recentActivities.map((activity) => {
                    const { icon, color } = getActivityIcon(activity);
                    return (
                      <div key={activity.id} className="d-flex align-items-start gap-2">
                        <div className={`bg-${color} bg-opacity-10 p-2 rounded-circle`}>
                          <i className={`${icon} text-${color}`}></i>
                        </div>
                        <div className="flex-fill">
                          <div className="fw-semibold">{activity.title}</div>
                          <div className="text-muted small">{activity.description}</div>
                          <small className="text-muted">{activity.time_ago || formatTimeAgo(activity.timestamp)}</small>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center text-muted py-3">
                    <i className="fas fa-info-circle mb-2 d-block"></i>
                    <small>No recent activities</small>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

    </Container>
  );
};

export default DistributorDashboard;